import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SingleTargetSelector } from '@/components/common/SingleTargetSelector';
import { useTimeSeriesChartController } from '@/features/time-series-chart/hooks/useTimeSeriesChartController';
import { useDiagramSlownessReport } from '@/features/time-series-chart/monitoring/hooks/useDiagramSlownessReport';

import { TimeSeriesChartErrorState } from '../time-series-chart-error-state/TimeSeriesChartErrorState';
import { TimeSeriesChartResultHeader } from '../time-series-chart-result-header/TimeSeriesChartResultHeader';
import { TimeSeriesChartResultPanel } from '../time-series-chart-result-panel/TimeSeriesChartResultPanel';
import { TimeSeriesChartSelectionPanel } from '../time-series-chart-selection-panel/TimeSeriesChartSelectionPanel';
import { TimeSeriesChartToolbar } from '../time-series-chart-toolbar/TimeSeriesChartToolbar';

import { ResultArea, SelectorArea, SelectorViewport, TimeSeriesChartRoot } from './TimeSeriesChartDesktop.style';

export function TimeSeriesChartDesktop() {
  const { t } = useTranslation();
  const { state, selection, seriesOptions, chartModel, isNavigationHintVisible, changeTarget, changeDates } =
    useTimeSeriesChartController();

  // Elements, not refs: the areas mount only after a target error clears, and the watchers must follow.
  const [selectorArea, setSelectorArea] = useState<HTMLElement | null>(null);
  const [resultArea, setResultArea] = useState<HTMLElement | null>(null);

  useDiagramSlownessReport({
    targetId: state.targetId,
    timeWindow: state.timeWindow,
    requestedSeriesIds: selection.selectedSeriesIds,
    chartElement: resultArea,
    selectorElement: selectorArea,
  });

  if (state.isTargetError) {
    return (
      <TimeSeriesChartRoot data-qa-element-id="time-series-chart">
        <TimeSeriesChartErrorState
          message={t('error.api.unknown')}
          isRetrying={state.isTargetRetrying}
          onRetry={state.retryTarget}
          data-qa-element-id="time-series-chart-target-error"
        />
      </TimeSeriesChartRoot>
    );
  }

  return (
    <TimeSeriesChartRoot data-qa-element-id="time-series-chart">
      <SelectorArea ref={setSelectorArea}>
        <SelectorViewport>
          {state.isChannelSelectorOpen ? (
            <TimeSeriesChartSelectionPanel
              seriesGroups={seriesOptions.groups}
              getSeriesColor={seriesOptions.getSeriesColor}
              selectedDates={state.selectedDates}
              onDateChange={changeDates}
              selectedSeriesIds={seriesOptions.validSelectedSeriesIds}
              onSelectedSeriesIdsChange={selection.changeSelectedSeries}
              isSeriesLoading={seriesOptions.isLoading}
              isSeriesError={seriesOptions.isError}
              onSeriesRetry={seriesOptions.retry}
              onBack={state.closeChannelSelector}
            />
          ) : (
            <SingleTargetSelector
              selectedTarget={state.selectedTarget}
              setSelectedTarget={changeTarget}
              searchText={state.searchText}
              setSearchText={state.changeSearchText}
            />
          )}
        </SelectorViewport>
      </SelectorArea>
      <ResultArea ref={setResultArea} data-qa-element-id="time-series-chart-result-panel">
        {state.targetId !== undefined && <TimeSeriesChartResultHeader targetName={state.selectedTarget?.name} />}
        <TimeSeriesChartToolbar
          selectedSeries={seriesOptions.selectedSeries}
          highlightedSeriesIds={selection.highlightedSeriesIds}
          getSeriesColor={seriesOptions.getSeriesColor}
          onHighlightToggle={selection.toggleHighlight}
          onDeselect={selection.deselectSeries}
          isNavigationHintVisible={isNavigationHintVisible}
        />
        <TimeSeriesChartResultPanel
          chart={chartModel.chart}
          previewSeriesId={chartModel.previewSeriesId}
          timeDomain={state.timeDomain}
          status={chartModel.status}
          onRetry={chartModel.retry}
        />
      </ResultArea>
    </TimeSeriesChartRoot>
  );
}
